import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { version } from '../../package.json';
import debug from 'debug';
import i18n from '../i18n';

// Configuración de logs con namespaces para mejor organización
const logManager = debug('rois:version:manager');
const logCheck = debug('rois:version:check');

// Habilitar logs solo en desarrollo
if (import.meta.env.DEV) {
  debug.enable('rois:version*');
}

// Types
import { AuthContextType } from '../hooks/useAuth';
import { Store } from 'redux';
import { RootState } from '../state/configureStore';

// Apollo Client
import { cacheApolloPersistor, client } from '../gql/client';

// GraphQL Operations (imports estáticos para evitar problemas de carga)
import { GET_CURRENT_VERSION } from '../gql/queries/version';

// Redux
import { persistor } from '../state/configureStore';
import { cleanState } from '../state/actions/main';
import { getInitialStatePersist } from '../state/selectors/persist';
import { openModal } from '../state/actions/ui/modal';

// Modal IDs
import { modalIds } from '../components/modals/SuperModal/types';

import { purgeSeedForCurrentUser } from '../gql/cacheSeed';
import { isBackendReachable } from '../offline/backendReachability';

// Version storage keys (rois_*) — anteriormente ora_* del proyecto legacy.
// El renombrado es transparente: VersionManager.migrateLegacyOraKeys() corre
// en el constructor y traslada cualquier valor existente bajo el nombre viejo
// al nuevo (one-shot, idempotente).
const VERSION_STORAGE_KEY = 'rois_app_version';
const LAST_CACHE_CLEAR_KEY = 'rois_last_cache_clear';

// Legacy keys del proyecto "ora" (antes de rois). Se conservan SOLO como
// referencia para la migración one-shot. NO usar para escribir.
const LEGACY_VERSION_STORAGE_KEY = 'ora_app_version';
const LEGACY_LAST_CACHE_CLEAR_KEY = 'ora_last_cache_clear';

// Sentinel en sessionStorage para suprimir un `onNeedRefresh` duplicado tras
// reload. Lo escribe `onContinue` del modal cuando inicia el flujo de
// actualización; `main.tsx#onNeedRefresh` lo lee en el siguiente boot. Si la
// edad del sentinel es < 30s, el callback se considera disparo duplicado
// (workbox-window vio SW v2 en `waiting` al registrar) y se descarta. El
// polling de cada 3 min recoge cualquier drift residual. Exportado para que
// main.tsx use exactamente la misma key.
export const VERSION_MODAL_RECENT_FIRE_KEY = 'rois_version_modal_recent_fire';
export const VERSION_MODAL_RECENT_FIRE_TTL_MS = 30_000;

interface VersionInfo {
  version: string;
  buildTime: string;
  environment: string;
}

interface CacheClearOptions {
  showModal?: boolean;
  force?: boolean;
  reason?: string;
  isNewVersion?: boolean;
  /**
   * Versión a la que se va a actualizar, cuando NO es la que corre.
   *
   * 🔴 El modal enseñaba siempre `this.currentVersion`, que es **la que se está
   * dejando**. En el camino del sondeo eso es engañoso de verdad: el aviso
   * llega mientras corre el bundle VIEJO, así que el usuario leía
   * «Nueva versión detectada: 2.0.2» y justo debajo «Versión: 2.0.1» — dos
   * números contradictorios en la misma ventana.
   *
   * En el arranque en frío no hace falta: allí el bundle nuevo YA está
   * cargado, así que `currentVersion` es la de destino.
   */
  targetVersion?: string;
}

/**
 * Configuración para detección automática de versiones via GitHub Deployments API
 */
interface GitHubDeploymentConfig {
  githubRepo: string; // 'owner/repo' - Requerido
  githubToken?: string; // Para acceso a repos privados
  environment?: string; // Entorno a monitorear (default: 'production')
  pollInterval?: number; // Intervalo de polling en ms (default: 5 min)
  enableAutoCheck?: boolean; // Si está habilitado el auto-check
}

/**
 * Información de despliegue de GitHub
 */
interface GitHubDeployment {
  id: string; // ✅ Actualizado a string para consistencia con backend
  sha: string;
  ref: string;
  environment: string;
  created_at: string;
  updated_at: string;
  statuses_url: string;
}

/**
 * Estado de despliegue
 */
interface DeploymentStatus {
  state: 'success' | 'failure' | 'error' | 'pending' | 'in_progress';
  description?: string;
  environment?: string;
  created_at: string;
}

/**
 * Sistema de gestión de versiones para ROIS
 * Detecta cambios de versión y gestiona la limpieza de cachés
 */
export class VersionManager {
  private static instance: VersionManager;
  private currentVersion: VersionInfo;
  private store?: Store<RootState>;
  private userData?: AuthContextType | null;

  private deploymentConfig: GitHubDeploymentConfig | null = null;
  private autoCheckInterval?: NodeJS.Timeout;

  /**
   * Guard de actualización en curso. `onContinue` tarda hasta ~9s (limpiezas
   * + espera de activación del SW + 500ms) y durante esa ventana el polling
   * de 3 min puede disparar `checkVersion` y abrir un SEGUNDO modal encima
   * del flujo ya consentido. Mientras esté a true, checkVersion no-opea.
   * No se resetea: la página muere en el reload.
   */
  private updateInFlight = false;

  /**
   * Función `updateSW` devuelta por vite-plugin-pwa al registrar el Service
   * Worker en modo `prompt`. Cuando el usuario confirma el modal de nueva
   * versión, llamamos a esta función para activar el SW v2 (skipWaiting)
   * antes del reload. Si no hay SW en waiting (caso polling-detectó-versión-
   * nueva pero SW aún no), la llamada es no-op y el reload natural sirve
   * los chunks nuevos desde la red.
   */
  private swUpdater?: (reloadPage?: boolean) => Promise<void>;

  private constructor() {
    // Migración one-shot de keys legacy ora_* → rois_*. Se ejecuta antes de
    // cualquier read/write para que getStoredVersionInfo() encuentre el valor
    // bajo el nombre nuevo y no dispare un falso "version changed" tras el
    // deploy que introduce el rename.
    VersionManager.migrateLegacyOraKeys();

    this.currentVersion = {
      version,
      buildTime: new Date().toISOString(),
      environment: import.meta.env.MODE,
    };
  }

  /**
   * Migra los valores almacenados bajo los nombres legacy `ora_*` a los
   * nombres `rois_*`. Idempotente: si ya migró, los reads de las legacy
   * darán null y la función no hace nada.
   *
   * Cubre tres keys:
   * - `ora_app_version`             → `rois_app_version`
   * - `ora_last_cache_clear`        → `rois_last_cache_clear`
   * - `ora_last_cache_clear_deployment` → `rois_last_cache_clear_deployment`
   *   (esta última se deriva de LAST_CACHE_CLEAR_KEY con sufijo `_deployment`)
   */
  private static migrateLegacyOraKeys(): void {
    if (typeof window === 'undefined' || !('localStorage' in window)) return;

    try {
      const pairs: Array<[legacy: string, current: string]> = [
        [LEGACY_VERSION_STORAGE_KEY, VERSION_STORAGE_KEY],
        [LEGACY_LAST_CACHE_CLEAR_KEY, LAST_CACHE_CLEAR_KEY],
        [
          `${LEGACY_LAST_CACHE_CLEAR_KEY}_deployment`,
          `${LAST_CACHE_CLEAR_KEY}_deployment`,
        ],
      ];

      let migratedCount = 0;
      for (const [legacyKey, currentKey] of pairs) {
        const legacyValue = localStorage.getItem(legacyKey);
        if (legacyValue === null) continue; // nada que migrar

        // Solo copiamos si el destino está vacío — no pisamos un valor más
        // reciente con uno antiguo si por algún motivo coexistieran.
        if (localStorage.getItem(currentKey) === null) {
          localStorage.setItem(currentKey, legacyValue);
        }
        localStorage.removeItem(legacyKey);
        migratedCount++;
      }

      if (migratedCount > 0) {
        logManager(
          `🔁 [migrateLegacyOraKeys] Migradas ${migratedCount} keys legacy ora_* → rois_*`
        );
      }
    } catch (error) {
      // Falla silenciosa: la migración es best-effort. El peor caso es un
      // único modal de "nueva versión" tras el deploy del rename.
      Sentry.captureException(error);
    }
  }

  /**
   * ¿Corremos dentro de la envoltura nativa (APK) en vez de un navegador?
   *
   * ─── Por qué este sistema entero se apaga ahí ──────────────────────────
   *
   * Este gestor detecta versiones nuevas comparando el bundle en ejecución con
   * lo que hay **desplegado en la web**, y las aplica limpiando cachés y
   * recargando la página. Dentro del APK los tres pasos son inútiles o dañinos:
   *
   *   1. No hay Service Worker que activar — Capacitor no los expone.
   *   2. `window.location.reload()` **vuelve a cargar el mismo bundle**: el que
   *      viene dentro del APK o el que instaló el OTA. La versión no cambia,
   *      así que el aviso reaparece en el siguiente sondeo. Bucle infinito.
   *   3. La limpieza que acompañaba a la recarga borraba las colas offline,
   *      OPFS (croquis) y la caché Apollo persistida: en una tablet de campo,
   *      el trabajo del operario todavía sin subir. Desde el 2026-10-01 solo
   *      limpia lo reconstruible (`clearRebuildableCaches`), pero el punto 2
   *      basta para no usar este camino dentro del APK.
   *
   * Quien actualiza la app es el OTA (`src/utils/appUpdate.ts`), y su autoridad
   * es el `latest.json` que publica la CI — no el deploy de Vercel, que puede ir
   * por delante del bundle que la flota tiene permitido instalar.
   *
   * Lo que NO se apaga: el camino manual (`forceCacheClear`, el botón "Limpiar
   * caché" del menú de usuario). Ahí el usuario pide limpiar a conciencia, y eso
   * sigue teniendo sentido dentro de la app. (El cierre de sesión vive en
   * `UserMenu/action.ts`; aquí hubo un `performLogout` sin llamantes que se
   * borró el 2026-10-08.)
   *
   * El gate vive aquí dentro y no en los llamantes a propósito: un sitio nuevo
   * que llame a `initialize()` queda cubierto sin que nadie tenga que acordarse.
   */
  private static isNativeShell(): boolean {
    try {
      return Capacitor.isNativePlatform();
    } catch {
      // Entornos sin el runtime de Capacitor (tests unitarios, SSR): web.
      return false;
    }
  }

  /**
   * Espera a que un Service Worker concreto alcance `activated` (o
   * `redundant`) — útil tras enviar `SKIP_WAITING` para no recargar
   * mientras SW v1 aún controla la página.
   *
   * Por qué importa: el `setTimeout(reload, 500)` del flujo onContinue
   * puede dispararse antes de que SW v2 termine de activar en máquinas
   * cargadas o redes lentas. Si recarga con SW v1 todavía activo, el
   * navegador sirve el bundle viejo desde la caché de v1, y la siguiente
   * página detecta SW v2 aún en `waiting` → workbox-window dispara
   * `waiting` (con `wasWaitingBeforeRegister:true`) → vite-pwa dispara
   * `onNeedRefresh` → segundo modal.
   *
   * Esta espera elimina la race: cuando volvemos del await, SW v2 está
   * `activated` y v1 `redundant`, así que la siguiente navegación queda
   * controlada por v2 y carga el bundle nuevo directamente.
   *
   * Timeout fallback: si la activación no completa en `timeoutMs`,
   * proseguimos igualmente. El sentinel `VERSION_MODAL_RECENT_FIRE_KEY`
   * actúa como segunda línea de defensa para esos casos extremos.
   */
  // public: también la usa el self-heal de main.tsx#onNeedRefresh cuando
  // suprime un modal post-reload (completa la activación sin re-preguntar).
  public static async waitForSWActivation(
    sw: ServiceWorker,
    timeoutMs: number
  ): Promise<void> {
    if (sw.state === 'activated' || sw.state === 'redundant') return;

    await new Promise<void>((resolve) => {
      const handler = () => {
        if (sw.state === 'activated' || sw.state === 'redundant') {
          sw.removeEventListener('statechange', handler);
          clearTimeout(timeout);
          resolve();
        }
      };
      sw.addEventListener('statechange', handler);
      const timeout = setTimeout(() => {
        sw.removeEventListener('statechange', handler);
        resolve();
      }, timeoutMs);
    });
  }

  public static getInstance(): VersionManager {
    if (!VersionManager.instance) {
      VersionManager.instance = new VersionManager();
    }
    return VersionManager.instance;
  }

  /**
   * Registra la función `updateSW` devuelta por vite-plugin-pwa
   * (`registerSW`). Llamada desde `main.tsx` justo después de registrar el
   * Service Worker. El versionManager la invoca en el `onContinue` del modal
   * para activar el SW v2 antes de recargar la página.
   */
  public setSWUpdater(
    updater: (reloadPage?: boolean) => Promise<void>
  ): void {
    this.swUpdater = updater;
    logManager('🔧 SW updater registered (prompt mode)');
  }

  /**
   * Inicializa el gestor de versiones.
   *
   * Cold-start check: si el bundle que se está ejecutando es distinto del
   * que este navegador procesó la última vez con éxito, YA corre la versión
   * nueva: solo se re-siembra la versión, **en silencio y en todos los
   * modos**, sin tocar la caché (ver el comentario del cuerpo). Cubre el caso
   * "pestaña en background → navegador descarta → SW v2 activa
   * silenciosamente → al volver, bundle nuevo". Hasta el 2026-10-01 aquí se
   * abría el aviso (solo en producción) y reaparecía en cada recarga y login
   * mientras no se aceptara.
   *
   * First-visit (no localStorage): sembrar silenciosamente sin modal — el
   * usuario nunca vio una versión "anterior", no hay nada que limpiar.
   *
   * 🔴 **Dentro de la app nativa esto NO abre modal.** Ver
   * {@link isNativeShell}: allí manda `latest.json`, no el deploy de la web.
   */
  public initialize(
    store: Store<RootState>,
    userData: AuthContextType | null = null
  ) {
    this.store = store;
    this.userData = userData;

    logManager(
      `🚀 ROIS Version Manager initialized - v${this.currentVersion.version}`
    );

    const stored = this.getStoredVersionInfo();

    // En la app nativa NO se compara ni se avisa: quien actualiza el bundle es
    // el OTA, y este camino no puede hacer nada por él.
    //
    // El valor SÍ se mantiene al día (no solo la primera vez): tras aplicar un
    // OTA el bundle es otro, y dejar aquí el número viejo sería guardar un dato
    // falso a la espera de que alguien lo lea y saque conclusiones.
    if (VersionManager.isNativeShell()) {
      if (!stored || this.hasVersionChanged(stored)) {
        this.updateStoredVersionInfo();
      }
      logManager(
        '📱 Cold-start: app nativa — sin modal de versión (manda el OTA)'
      );
      return;
    }

    // First-visit seed: sembrar localStorage en TODOS los modos. Inofensivo
    // y necesario para que la próxima visita tenga un punto de comparación
    // independientemente del MODE. El gate de "no prompts fuera de prod"
    // sólo aplica al fire del modal (abajo).
    if (!stored) {
      this.updateStoredVersionInfo();
      logManager('🌱 Cold-start: first-visit, sembrando localStorage sin modal');
      return;
    }

    if (!this.hasVersionChanged(stored)) {
      logManager('✅ Cold-start: bundle coincide con stored');
      return;
    }

    // Versión distinta a la última procesada: este navegador YA corre la nueva
    // (el SW la activó con la pestaña cerrada, o se recargó). No hay nada que
    // ofrecer: se re-siembra la versión, en silencio y en todos los modos.
    // Antes se abría el aviso —y en cada recarga y en cada login mientras no se
    // aceptara—; en beta, además, la versión no se re-sembraba nunca y
    // «Limpiar caché» salía con la cara de «Nueva versión» (spec 2026-09-30 § 5.2).
    //
    // 🔴 Sin limpiar nada, igual que la APK tras un OTA. Cuando esto corre, el
    // router ya lanzó sus loaders con la caché restaurada: vaciar Apollo aquí
    // rechaza las consultas en vuelo («Store reset while query was in flight»)
    // y deja pantallas sin datos. Y un arranque sin cobertura —el SW activó la
    // versión nueva con la pestaña cerrada— se quedaría sin la semilla, que es
    // lo único que le deja trabajar.
    logManager(
      `🆕 Cold-start: ${stored.version} → ${this.currentVersion.version} — re-siembra silenciosa`
    );
    Sentry.addBreadcrumb({
      category: 'version-manager',
      level: 'info',
      message: 'cold-start mismatch (silent)',
      data: {
        bundled: this.currentVersion.version,
        stored: stored.version,
        environment: this.currentVersion.environment,
      },
    });
    this.updateStoredVersionInfo();
  }

  /**
   * Verifica si hay una nueva versión y limpia cachés si es necesario
   */
  public async checkVersion(options: CacheClearOptions = {}): Promise<boolean> {
    try {
      if (this.updateInFlight) {
        logCheck(
          '⏸️ [checkVersion] update ya en curso (onContinue) — se ignora para no abrir un segundo modal'
        );
        return false;
      }
      logCheck(`🔍 [checkVersion] Called with options:`, options);
      const storedVersionInfo = this.getStoredVersionInfo();
      const hasVersionChanged = this.hasVersionChanged(storedVersionInfo);

      logCheck(
        `🔍 [checkVersion] hasVersionChanged: ${hasVersionChanged}, force: ${options.force}`
      );

      if (hasVersionChanged || options.force) {
        logCheck(
          `🔄 [checkVersion] Version change detected: ${
            storedVersionInfo?.version || 'none'
          } → ${this.currentVersion.version}`
        );

        const reason =
          options.reason ||
          (hasVersionChanged
            ? i18n.t('errors:newVersionDetected')
            : i18n.t('errors:forcedCleanup'));

        logCheck(`🔍 [checkVersion] showModal option: ${options.showModal}`);

        if (options.showModal !== false) {
          const isNewVersion = options.isNewVersion ?? hasVersionChanged;
          logCheck(
            `🚀 [checkVersion] Calling showVersionUpdateModal with reason: "${reason}", isNewVersion: ${isNewVersion}`
          );
          await this.showVersionUpdateModal(
            reason,
            isNewVersion,
            options.targetVersion
          );
          logCheck(`✅ [checkVersion] showVersionUpdateModal completed`);
        } else {
          logCheck(
            `⏭️ [checkVersion] Skipping modal, clearing caches directly`
          );
          await this.clearRebuildableCaches();
          this.updateStoredVersionInfo();
        }

        return true;
      }

      logCheck(`⭕ [checkVersion] No version change detected, returning false`);
      return false;
    } catch (error) {
      logCheck('❌ [checkVersion] Error:', error);
      Sentry.captureException(error);
      return false;
    }
  }

  /**
   * Obtiene la información de versión almacenada
   */
  private getStoredVersionInfo(): VersionInfo | null {
    logCheck(`🔍 [getStoredVersionInfo] === GETTING STORED VERSION ===`);
    try {
      const stored = localStorage.getItem(VERSION_STORAGE_KEY);
      logCheck(`🔍 [getStoredVersionInfo] Raw localStorage value:`, stored);
      logCheck(
        `🔍 [getStoredVersionInfo] VERSION_STORAGE_KEY:`,
        VERSION_STORAGE_KEY
      );

      if (!stored) {
        logCheck(
          `🔍 [getStoredVersionInfo] No stored version found - RETURNING NULL`
        );
        return null;
      }

      const parsed = JSON.parse(stored) as VersionInfo;
      logCheck(`🔍 [getStoredVersionInfo] Parsed version object:`, parsed);
      logCheck(`🔍 [getStoredVersionInfo] === RETURNING PARSED VERSION ===`);
      return parsed;
    } catch (error) {
      logCheck(
        '❌ [getStoredVersionInfo] Error reading stored version:',
        error
      );
      return null;
    }
  }

  /**
   * Verifica si la versión ha cambiado
   */
  private hasVersionChanged(storedVersion: VersionInfo | null): boolean {
    logCheck(`🔍 [hasVersionChanged] === VERSION CHANGE CHECK ===`);

    if (!storedVersion) {
      logCheck(
        '🆕 [hasVersionChanged] No stored version found - RETURNING TRUE'
      );
      return true; // Primera vez que se ejecuta
    }

    logCheck(`🔍 [hasVersionChanged] Stored version object:`, storedVersion);
    logCheck(
      `🔍 [hasVersionChanged] Current version object:`,
      this.currentVersion
    );
    logCheck(
      `🔍 [hasVersionChanged] Stored version.version: "${storedVersion.version}"`
    );
    logCheck(
      `🔍 [hasVersionChanged] Current version.version: "${this.currentVersion.version}"`
    );
    logCheck(
      `🔍 [hasVersionChanged] Stored environment: "${storedVersion.environment}"`
    );
    logCheck(
      `🔍 [hasVersionChanged] Current environment: "${this.currentVersion.environment}"`
    );

    const versionChanged =
      storedVersion.version !== this.currentVersion.version;
    const environmentChanged =
      storedVersion.environment !== this.currentVersion.environment;
    const hasChanged = versionChanged || environmentChanged;

    logCheck(
      `🔍 [hasVersionChanged] Version comparison: "${storedVersion.version}" !== "${this.currentVersion.version}" = ${versionChanged}`
    );
    logCheck(
      `🔍 [hasVersionChanged] Environment comparison: "${storedVersion.environment}" !== "${this.currentVersion.environment}" = ${environmentChanged}`
    );
    logCheck(
      `🔍 [hasVersionChanged] Final result: ${versionChanged} || ${environmentChanged} = ${hasChanged}`
    );
    logCheck(`🔍 [hasVersionChanged] === RETURNING: ${hasChanged} ===`);

    return hasChanged;
  }

  /**
   * Actualiza la información de versión almacenada
   */
  private updateStoredVersionInfo(): void {
    try {
      localStorage.setItem(
        VERSION_STORAGE_KEY,
        JSON.stringify(this.currentVersion)
      );
      localStorage.setItem(LAST_CACHE_CLEAR_KEY, new Date().toISOString());
      logManager(`💾 Version info updated: v${this.currentVersion.version}`);
    } catch (error) {
      logManager('❌ Error updating stored version:');
      Sentry.captureException(error);
    }
  }

  /**
   * Muestra modal de actualización de versión usando el sistema de modales Redux
   */
  private async showVersionUpdateModal(
    reason: string,
    isNewVersion: boolean = true,
    targetVersion?: string
  ): Promise<void> {
    logCheck(`🚀 [showVersionUpdateModal] Starting with reason: "${reason}", isNewVersion: ${isNewVersion}`);

    return new Promise((resolve) => {
      logCheck(`🔍 [showVersionUpdateModal] Checking store availability...`);
      if (!this.store) {
        logCheck('❌ [showVersionUpdateModal] Store not available for modal!');
        resolve();
        return;
      }

      logCheck(`✅ [showVersionUpdateModal] Store is available`);
      logCheck(
        `🔍 [showVersionUpdateModal] Current version:`,
        this.currentVersion.version
      );
      logCheck(`🔍 [showVersionUpdateModal] Modal reason:`, reason);

      try {
        logCheck(`🚀 [showVersionUpdateModal] Dispatching openModal action...`);
        this.store.dispatch(
          openModal(modalIds.VERSION_UPDATE_MODAL, {
            // La de DESTINO cuando la conocemos (sondeo); si no, la que corre
            // —que en el arranque en frío ya es la nueva—. Ver `targetVersion`.
            version: targetVersion ?? this.currentVersion.version,
            reason,
            isNewVersion,
            onContinue: async () => {
              logCheck(`🎬 [showVersionUpdateModal] Modal onContinue called`);

              await this.runUpdateFlow(isNewVersion);
              resolve();
            },
          })
        );
        logCheck(
          `✅ [showVersionUpdateModal] openModal action dispatched successfully`
        );
      } catch (error) {
        logCheck(`❌ [showVersionUpdateModal] Error dispatching modal:`, error);
        resolve();
      }
    });
  }

  /**
   * Aplica una versión (o la limpieza manual) y recarga. Lo comparten el modal
   * de «Limpiar caché» y el aviso web de `useAppUpdate` (`applyWebUpdate`).
   *
   * @param isNewVersion `true` = actualización (borra la versión guardada para
   *   que el siguiente arranque re-siembre con la que cargue de verdad);
   *   `false` = limpieza manual (re-siembra con la actual y resetea la interfaz).
   */
  private async runUpdateFlow(isNewVersion: boolean): Promise<void> {
    // Doble click / doble disparo del close-handler del SuperModal:
    // el flujo solo debe correr una vez.
    if (this.updateInFlight) {
      logCheck('⏸️ onContinue duplicado ignorado (updateInFlight)');
      return;
    }
    this.updateInFlight = true;
    // El tick del polling de 3 min no debe abrir OTRO modal mientras
    // limpiamos cachés y esperamos la activación del SW.
    this.stopAutoVersionCheck();

    // 🔒 PRIMERO: registrar la versión procesada — con un matiz
    // crítico para evitar el DOBLE-MODAL:
    //
    // En un update de versión real (isNewVersion=true), el paso 3
    // (swUpdater → skipWaiting) ACTIVA el SW v2, de modo que tras el
    // reload del paso 4 arranca un bundle DISTINTO (más nuevo) del
    // que corre ahora mismo. Si persistiéramos `this.currentVersion`
    // (el bundle ANTIGUO), el cold-start check de initialize() en el
    // próximo boot vería stored(viejo) != bundle(nuevo) y
    // RE-dispararía el modal — el usuario lo ve dos veces.
    //
    // Solución: en isNewVersion borramos la key de versión. El boot
    // post-reload tomará el seed path de initialize() y adoptará en
    // silencio la versión que REALMENTE cargue, sea cual sea, sin
    // segundo modal.
    //
    // En limpieza manual (isNewVersion=false) el bundle NO cambia,
    // así que registramos la versión actual con normalidad.
    if (isNewVersion) {
      localStorage.removeItem(VERSION_STORAGE_KEY);
      logCheck(
        '🧹 Version key removed (isNewVersion) — seed adoptará el bundle post-reload'
      );
    } else {
      this.updateStoredVersionInfo();
      logCheck('✅ Version info updated before cache clear');
    }

    // 🧹 SEGUNDO: limpiar SOLO lo reconstruible (ver
    //    clearRebuildableCaches). En la limpieza manual se reinicia
    //    además el estado de interfaz persistido (filtros, pestañas):
    //    es lo que el usuario pide al pulsar «Limpiar caché».
    await this.clearRebuildableCaches();
    if (!isNewVersion) await this.clearReduxCache();

    // 🛰️ TERCERO: Activar el SW v2 si hay uno en estado `waiting`.
    //
    //    🔴 MEDIDO el 2026-10-01 (build de producción, SW real, dos
    //    pestañas, spec 2026-09-30 § 5.7):
    //    - Al aplicar en una pestaña, la OTRA se recarga sola y queda en la
    //      versión nueva: vite-plugin-pwa (`register.js`) recarga al ver
    //      cambiar el controlador. Hasta esa medición este comentario decía
    //      lo contrario (que en modo `prompt` no había `controllerchange`).
    //    - La recarga de ESTA pestaña la hace el paso 4; `updateSW()` aquí
    //      solo manda SKIP_WAITING al SW v2 en `waiting` para que, al
    //      recargar, ya esté activo y sirva los chunks nuevos.
    //    - Sin SW en `waiting`, `messageSkipWaiting` no hace nada y aceptar
    //      carga igualmente la versión nueva a la primera.
    //    - La precache sigue presente después: ya NO se vacía en el paso 2
    //      (spec 2026-09-30 § 5.3); borrarla dejaba la web sin arranque sin
    //      cobertura hasta el siguiente despliegue.
    //
    //    Si alguien añade `clientsClaim: true` al config de workbox, el
    //    cambio de controlador llegaría antes que el paso 4 y habría que
    //    deduplicar las dos recargas.
    if (this.swUpdater) {
      try {
        // Capturar el SW v2 que estaba en `waiting` ANTES de enviar
        // SKIP_WAITING, para poder esperar a que termine de activar.
        // Si no esperamos, el reload de abajo puede dispararse mientras
        // SW v1 sigue activo → el navegador sirve el bundle VIEJO desde
        // la caché de v1 → workbox-window en la nueva página detecta
        // SW v2 aún en waiting → dispara evento `waiting` con
        // `wasWaitingBeforeRegister: true` → vite-pwa lo traduce en
        // `onNeedRefresh` → DOBLE MODAL (la causa del bug reportado
        // en 1.4.0 / 1.4.1 tras el fix de PR #30 que ya borraba la
        // version key — esa borraba el path cold-start pero no este).
        //
        // Verificado en `node_modules/workbox-window/Workbox.js` y en
        // `node_modules/vite-plugin-pwa/dist/client/build/register.js`.
        let waitingSW: ServiceWorker | null = null;
        if (
          typeof navigator !== 'undefined' &&
          'serviceWorker' in navigator
        ) {
          const reg =
            await navigator.serviceWorker.getRegistration();
          waitingSW = reg?.waiting ?? null;
        }

        await this.swUpdater();
        logCheck('✅ Service Worker SKIP_WAITING enviado');

        if (waitingSW) {
          await VersionManager.waitForSWActivation(waitingSW, 8000);
          logCheck(
            `✅ SW v2 transición completa (estado: ${waitingSW.state})`
          );
        } else {
          logCheck(
            '⏭️ No había SW en waiting; salto la espera de activación'
          );
        }
      } catch (error) {
        logCheck('⚠️ Service Worker update failed:', error);
        Sentry.captureException(error);
      }
    } else {
      logCheck(
        '⏭️ swUpdater not registered (SW unsupported o pre-registro)'
      );
    }

    // Sentinel en sessionStorage para que `onNeedRefresh` en el boot
    // post-reload suprima el segundo modal si workbox detecta a SW v2
    // aún en `waiting` (caso de fallback: la espera de activación
    // expiró por timeout o falló silenciosamente). El polling cada
    // 3 min recoge cualquier drift restante.
    //
    // Sólo aplica al flujo de actualización (isNewVersion=true). En
    // limpieza manual no hay transición de SW.
    if (isNewVersion) {
      try {
        sessionStorage.setItem(
          VERSION_MODAL_RECENT_FIRE_KEY,
          String(Date.now())
        );
      } catch {
        // sessionStorage podría no estar disponible (ej. modo privado
        // estricto). El fix de Layer 1 (espera de activación) es la
        // protección principal; este sentinel es defensa en
        // profundidad — fallar aquí no rompe nada.
      }
    }


    // 🔄 CUARTO: Recargar página. Es la fuente de verdad final —
    //    cubre tanto el caso "SW v2 activó y necesita reload"
    //    como "polling detectó nueva versión pero SW no estaba".
    logCheck(
      `🔄 [showVersionUpdateModal] Reloading page in 500ms...`
    );
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }

  /**
   * Aplica la versión que sirve el dominio (aviso web de `useAppUpdate`, que
   * la ofrece con la misma política que en la APK). Limpia solo lo
   * reconstruible: las colas, borradores y fotos pendientes se conservan.
   */
  public applyWebUpdate(): Promise<void> {
    return this.runUpdateFlow(true);
  }

  /**
   * Limpia SOLO lo que se reconstruye desde el servidor: la caché de Apollo
   * (en memoria y la persistida de la pestaña) y la semilla de arranque en frío
   * del usuario, que si no se purga restaura la caché vieja en el siguiente
   * arranque.
   *
   * 🔴 No toca el trabajo del usuario: colas sin conexión (la compuesta lleva
   * las fotos del parte dentro), OPFS, borradores, escalera del aviso ni la
   * precache del SW. Antes se borraba todo «para evitar replay contra un schema
   * nuevo», pero la cola simple reenvía el documento tal cual (manda el schema
   * del backend) y la compuesta ya lee entradas de versiones anteriores; la APK
   * conserva las colas en cada OTA desde siempre. Solo el logout purga trabajo
   * (tablet compartida entre usuarios). Investigación: spec 2026-09-30 § 3.
   *
   * 🔴 Sin cobertura la semilla SE CONSERVA: es lo que deja arrancar con datos
   * tras la recarga. Purgarla sin red dejaba la app sin catálogos hasta que
   * volviera la cobertura —y el aviso de «Limpiar caché» promete que los datos
   * no se verán afectados—. `navigator.onLine` solo es fiable cuando dice
   * `false`; el backend inalcanzable lo sabe `backendReachability`.
   */
  public async clearRebuildableCaches(): Promise<void> {
    await this.clearApolloCache();
    const offline =
      (typeof navigator !== 'undefined' && navigator.onLine === false) ||
      isBackendReachable() === false;
    if (offline) {
      logCheck('📴 Sin cobertura: la semilla se conserva para arrancar con datos');
      return;
    }
    try {
      await purgeSeedForCurrentUser();
    } catch (error) {
      logCheck('❌ Error purging Apollo seed:');
      Sentry.captureException(error);
    }
  }

  /**
   * Limpia la caché de Apollo Client (basado en el logout exitoso)
   */
  private async clearApolloCache(): Promise<void> {
    try {
      await client.clearStore();
      await client.cache.reset({ discardWatches: true }); // 🔥 CLAVE - elimina watchers activos
      await cacheApolloPersistor.purge();
      logCheck('✅ Apollo Client cache cleared');
    } catch (error) {
      logCheck('❌ Error clearing Apollo cache:');
      Sentry.captureException(error);
    }
  }

  /**
   * Limpia el estado persistente de Redux
   */
  private async clearReduxCache(): Promise<void> {
    try {
      if (this.store) {
        // Limpiar solo el estado específico que necesita ser reiniciado
        const currentState = this.store.getState();
        const stateToPersist = getInitialStatePersist(currentState);

        // Despachar acción para limpiar estado
        this.store.dispatch(cleanState(stateToPersist));
      }

      // Purgar el persistor
      await persistor.purge();
      logCheck('✅ Redux persistent state cleared');
    } catch (error) {
      logCheck('❌ Error clearing Redux cache:');
      Sentry.captureException(error);
    }
  }

  /**
   * Fuerza la limpieza de cachés (para uso manual)
   */
  public async forceCacheClear(
    reason = i18n.t('errors:manualCleanup')
  ): Promise<void> {
    await this.checkVersion({ force: true, reason, showModal: true });
  }

  /**
   * Configura la detección automática de versiones via GitHub Deployments API
   */
  configureGitHubDeploymentDetection(config: GitHubDeploymentConfig): void {
    logCheck(`🔧 Configuring GitHub Deployment detection...`);
    logCheck(`📋 Input config:`, config);

    // Segundo cierre del gate nativo (ver `isNativeShell`). El cold-start ya
    // sale antes en `initialize()`, pero este sondeo tiene su propia entrada y
    // era el que mantenía vivo el bucle del modal dentro del APK: cada 3 min
    // comparaba contra la versión desplegada en la WEB, que en la app no
    // significa nada.
    const nativeShell = VersionManager.isNativeShell();

    this.deploymentConfig = {
      environment: 'production',
      pollInterval: 5 * 60 * 1000, // 5 minutos por defecto
      enableAutoCheck: false,
      ...config,
      ...(nativeShell ? { enableAutoCheck: false } : {}),
    };

    if (nativeShell) {
      logCheck(
        '📱 App nativa — polling de versión desactivado (actualiza el OTA)'
      );
    }

    logCheck(`✅ GitHub Deployment detection configured!`);
    logCheck(`📊 Final config:`, {
      repo: this.deploymentConfig.githubRepo,
      environment: this.deploymentConfig.environment,
      pollIntervalMinutes: (this.deploymentConfig.pollInterval || 0) / 60000,
      enableAutoCheck: this.deploymentConfig.enableAutoCheck,
      hasToken: !!this.deploymentConfig.githubToken,
    });

    // Si se habilita, iniciar polling automático
    if (this.deploymentConfig.enableAutoCheck) {
      logCheck(`🚀 Auto-check is ENABLED - starting polling...`);
      this.startAutoVersionCheck();
    } else {
      logCheck(`⏸️  Auto-check is DISABLED - not starting polling`);
      this.stopAutoVersionCheck();
    }
  }

  /**
   * Inicia el polling automático de GitHub Deployments
   */
  private startAutoVersionCheck(): void {
    this.stopAutoVersionCheck(); // Limpiar interval anterior

    if (!this.deploymentConfig) {
      logCheck('🚨 No deployment config found for auto-check');
      return;
    }

    const intervalSeconds =
      (this.deploymentConfig.pollInterval || 300000) / 1000;
    logCheck(`🚀 Starting auto deployment check every ${intervalSeconds}s`);
    logCheck(`🔧 Config details:`, {
      repo: this.deploymentConfig.githubRepo,
      env: this.deploymentConfig.environment,
      hasToken: !!this.deploymentConfig.githubToken,
      intervalMs: this.deploymentConfig.pollInterval,
    });

    let pollCount = 0;
    this.autoCheckInterval = setInterval(async () => {
      pollCount++;
      logCheck(`�� [POLL #${pollCount}] === STARTING AUTO-CHECK (BACKEND) ===`);
      logCheck(`🔄 [POLL #${pollCount}] Config:`, {
        repo: this.deploymentConfig?.githubRepo,
        env: this.deploymentConfig?.environment,
        interval: this.deploymentConfig?.pollInterval,
      });

      try {
        const startTime = Date.now();
        // ✅ CAMBIO: Usar backend en lugar de GitHub directo
        const backendResult = await this.checkBackendVersion();
        const duration = Date.now() - startTime;

        logCheck(
          `⚡ [POLL #${pollCount}] Backend check completed in ${duration}ms`
        );
        logCheck(`📊 [POLL #${pollCount}] Result:`, backendResult);

        if (backendResult.hasNewVersion) {
          logCheck(`🎉 [POLL #${pollCount}] === NEW VERSION FROM BACKEND ===`);
          logCheck(
            `📦 [POLL #${pollCount}] Version Info:`,
            backendResult.versionInfo
          );

          // Nueva versión detectada automáticamente
          logCheck(`🚀 [POLL #${pollCount}] Showing update modal...`);

          // Debug store availability
          logCheck(`🔍 [POLL #${pollCount}] === STORE DEBUG INFO ===`);
          logCheck(`🔍 [POLL #${pollCount}] this.store exists:`, !!this.store);
          logCheck(
            `🔍 [POLL #${pollCount}] this.store type:`,
            typeof this.store
          );
          if (this.store) {
            logCheck(
              `🔍 [POLL #${pollCount}] store.dispatch type:`,
              typeof this.store.dispatch
            );
            logCheck(
              `🔍 [POLL #${pollCount}] store.getState type:`,
              typeof this.store.getState
            );
            try {
              const state = this.store.getState();
              logCheck(
                `🔍 [POLL #${pollCount}] store state available:`,
                !!state
              );
              logCheck(`🔍 [POLL #${pollCount}] modal state:`, state?.modal);
            } catch (error) {
              logCheck(
                `❌ [POLL #${pollCount}] Error accessing store state:`,
                error
              );
            }
          }
          logCheck(`🔍 [POLL #${pollCount}] ========================`);

          // 🎯 FIX: Forzar modal cuando polling detecta nueva versión
          logCheck(
            `🎯 [POLL #${pollCount}] FORCING version update modal - new version from backend!`
          );
          await this.checkVersion({
            force: true, // ← CLAVE: Forzar porque tenemos nueva versión del backend
            showModal: true,
            isNewVersion: true, // ← Explícito: es una versión nueva real
            // El número va en la línea «Versión: X» del modal y NO aquí: antes
            // salía en los dos sitios y con valores distintos —el de arriba la
            // nueva, el de abajo la vieja—, que es peor que no ponerlo.
            reason: i18n.t('errors:newVersionDetected'),
            targetVersion: backendResult.versionInfo?.version,
          });

          // Actualizar timestamp después de procesar
          this.updateLastDeploymentCheckTime();
          logCheck(`✅ [POLL #${pollCount}] === UPDATE PROCESS COMPLETED ===`);
        } else {
          logCheck(
            `⭕ [POLL #${pollCount}] No new version - next check in ${intervalSeconds}s`
          );
        }
      } catch (error) {
        logCheck(`❌ [POLL #${pollCount}] ERROR:`, error);
        logCheck(
          `🔄 [POLL #${pollCount}] Will retry in ${intervalSeconds}s...`
        );
      }

      logCheck(`🔄 [POLL #${pollCount}] === POLL COMPLETED ===\n`);
    }, this.deploymentConfig.pollInterval);

    logCheck(
      `✅ Auto-check interval started. Interval ID: ${this.autoCheckInterval}`
    );
    logCheck(`⏰ Next check scheduled in ${intervalSeconds}s`);
  }

  /**
   * Detiene el polling automático
   */
  private stopAutoVersionCheck(): void {
    if (this.autoCheckInterval) {
      logCheck(
        `🛑 Stopping auto deployment check (Interval ID: ${this.autoCheckInterval})`
      );
      clearInterval(this.autoCheckInterval);
      this.autoCheckInterval = undefined;
      logCheck('✅ Auto deployment check stopped successfully');
    } else {
      logCheck(
        '⚠️ Auto deployment check was not running (no interval to stop)'
      );
    }
  }

  /**
   * ESTRATEGIA PRINCIPAL: Backend API (más eficiente que GitHub directamente)
   * El backend consulta GitHub cada 5 minutos y almacena en BD
   * VERSION: 2025-01-02-v3 (Backend-driven version checking)
   */
  async checkBackendVersion(): Promise<{
    hasNewVersion: boolean;
    versionInfo?: {
      deploymentId: string;
      sha: string;
      ref: string;
      environment: string;
      createdAt: string;
      updatedAt: string;
      version?: string;
      deployedAt?: string;
    };
  }> {
    logCheck('🔄 [VERSION 2025-01-02-v3] Running checkBackendVersion...');

    if (!this.deploymentConfig) {
      logCheck('🚨 Version detection not configured');
      return { hasNewVersion: false };
    }

    const { environment = 'production' } = this.deploymentConfig;

    try {
      logCheck(`🔍 Checking backend for ${environment} version...`);

      // Usar Apollo Client importado estáticamente
      logCheck('🔍 Using statically imported client and GET_CURRENT_VERSION');

      // Consultar versión desde el backend
      const { data } = await client.query({
        query: GET_CURRENT_VERSION,
        variables: { environment },
        fetchPolicy: 'network-only', // Siempre obtener datos frescos
      });

      if (!data?.currentVersion) {
        logCheck('⚠️ No version info found in backend');
        return { hasNewVersion: false };
      }

      const backendVersionInfo = data.currentVersion;
      logCheck(
        `📦 Backend version: ${backendVersionInfo.version} (deployment: ${backendVersionInfo.deploymentId})`
      );

      // Comparar con versión local
      const currentVersion = this.currentVersion.version;
      logCheck(
        `🆚 Comparing: ${backendVersionInfo.version} vs local ${currentVersion}`
      );

      const hasNewVersion = this.isNewerVersion(
        backendVersionInfo.version,
        currentVersion
      );

      if (hasNewVersion) {
        logCheck(`✅ NEW VERSION DETECTED FROM BACKEND!`);
        return {
          hasNewVersion: true,
          versionInfo: backendVersionInfo,
        };
      } else {
        logCheck(`⭕ Same version - no update needed`);
      }

      return { hasNewVersion: false };
    } catch (error) {
      logCheck('❌ Error checking backend version:');

      // Fallback: continuar con el sistema anterior si hay problemas
      logCheck('🔄 Falling back to GitHub API check...');
      return this.checkGitHubDeploymentsFallback();
    }
  }

  /**
   * @deprecated Use checkBackendVersion() instead. This method is maintained for backward compatibility.
   * ESTRATEGIA SECUNDARIA: GitHub Deployments API directo
   * Detecta cuando un deployment se marca como "success"
   * VERSION: 2025-01-02-v2 (Deprecated - use backend instead)
   */
  async checkGitHubDeployments(): Promise<{
    hasNewVersion: boolean;
    deployment?: GitHubDeployment;
  }> {
    logCheck(
      '⚠️ [DEPRECATED] checkGitHubDeployments called - consider using checkBackendVersion()'
    );

    // Intentar usar el backend primero
    try {
      const backendResult = await this.checkBackendVersion();

      // Convertir el resultado del backend al formato esperado por compatibilidad
      if (backendResult.hasNewVersion && backendResult.versionInfo) {
        const mockDeployment: GitHubDeployment = {
          id: backendResult.versionInfo.deploymentId,
          sha: backendResult.versionInfo.sha,
          ref: backendResult.versionInfo.ref,
          environment: backendResult.versionInfo.environment,
          created_at:
            backendResult.versionInfo.deployedAt ||
            backendResult.versionInfo.createdAt,
          updated_at: backendResult.versionInfo.updatedAt,
          statuses_url: `https://api.github.com/repos/${this.deploymentConfig?.githubRepo}/deployments/${backendResult.versionInfo.deploymentId}/statuses`,
        };

        return {
          hasNewVersion: true,
          deployment: mockDeployment,
        };
      }

      return { hasNewVersion: false };
    } catch (error) {
      logCheck(
        '⚠️ Backend version check failed, falling back to direct GitHub API'
      );
      return this.checkGitHubDeploymentsFallback();
    }
  }

  /**
   * FALLBACK: GitHub Deployments API directo (mantener por compatibilidad)
   */
  private async checkGitHubDeploymentsFallback(): Promise<{
    hasNewVersion: boolean;
    deployment?: GitHubDeployment;
  }> {
    logCheck('🔄 [FALLBACK] Running direct GitHub API check...');

    if (!this.deploymentConfig) {
      return { hasNewVersion: false };
    }

    const {
      githubRepo,
      githubToken,
      environment = 'production',
    } = this.deploymentConfig;

    try {
      const headers: Record<string, string> = {
        Accept: 'application/vnd.github+json',
        'X-GitHub-Api-Version': '2022-11-28',
      };

      if (githubToken) {
        headers['Authorization'] = `Bearer ${githubToken}`;
      }

      const deploymentsResponse = await fetch(
        `https://api.github.com/repos/${githubRepo}/deployments?environment=${environment}&per_page=3`,
        { headers }
      );

      if (!deploymentsResponse.ok) {
        return { hasNewVersion: false };
      }

      const deployments: GitHubDeployment[] = await deploymentsResponse.json();

      if (deployments.length === 0) {
        return { hasNewVersion: false };
      }

      const latestDeployment = deployments[0];
      const statusResponse = await fetch(latestDeployment.statuses_url, {
        headers,
      });

      if (!statusResponse.ok) {
        return { hasNewVersion: false };
      }

      const statuses: DeploymentStatus[] = await statusResponse.json();

      if (statuses.length === 0 || statuses[0].state !== 'success') {
        return { hasNewVersion: false };
      }

      const hasNewVersion = this.isNewerVersion(
        latestDeployment.ref,
        this.currentVersion.version
      );

      return hasNewVersion
        ? { hasNewVersion: true, deployment: latestDeployment }
        : { hasNewVersion: false };
    } catch (error) {
      logCheck('❌ Fallback GitHub check failed:');
      return { hasNewVersion: false };
    }
  }

  /**
   * Verificación manual de nuevos deployments (actualizado para usar backend)
   */
  async checkForNewDeployment(): Promise<boolean> {
    try {
      const result = await this.checkBackendVersion();
      if (result.hasNewVersion) {
        logCheck('🎉 New version detected from backend:', result.versionInfo);
        this.updateLastDeploymentCheckTime();
      }
      return result.hasNewVersion;
    } catch (error) {
      logCheck('Error en checkForNewDeployment:');
      return false;
    }
  }

  /**
   * Obtiene el timestamp de la última verificación de deployment
   */
  private getLastDeploymentCheckTime(): number {
    const stored = localStorage.getItem(`${LAST_CACHE_CLEAR_KEY}_deployment`);
    return stored ? parseInt(stored, 10) : 0;
  }

  /**
   * Actualiza el timestamp de la última verificación de deployment
   */
  private updateLastDeploymentCheckTime(): void {
    const now = Date.now();
    localStorage.setItem(`${LAST_CACHE_CLEAR_KEY}_deployment`, now.toString());
    logCheck(
      `💾 Last deployment check time updated: ${new Date(now).toISOString()}`
    );
  }

  /**
   * Compara dos versiones para determinar si deploymentVersion es más nueva que currentVersion
   * Soporta formatos: v2.0.3-beta.7, 2.0.3-beta.6, etc.
   */
  private isNewerVersion(
    deploymentRef: string,
    currentVersion: string
  ): boolean {
    try {
      // Limpiar prefijos 'v' y 'refs/tags/' de deploymentRef
      const cleanDeploymentVersion = deploymentRef
        .replace(/^refs\/tags\//, '')
        .replace(/^v/, '');

      // Limpiar prefijo 'v' de currentVersion
      const cleanCurrentVersion = currentVersion.replace(/^v/, '');

      logCheck(`�� Comparing versions:`);
      logCheck(
        `   Deployment: "${deploymentRef}" → "${cleanDeploymentVersion}"`
      );
      logCheck(`   Current:    "${currentVersion}" → "${cleanCurrentVersion}"`);

      // Si las versiones son exactamente iguales, no es nueva
      if (cleanDeploymentVersion === cleanCurrentVersion) {
        logCheck(`📊 Versions are identical`);
        return false;
      }

      // Usar semver-like comparison
      const deploymentParts = this.parseVersion(cleanDeploymentVersion);
      const currentParts = this.parseVersion(cleanCurrentVersion);

      logCheck(`📊 Parsed deployment:`, deploymentParts);
      logCheck(`📊 Parsed current:`, currentParts);

      // Comparar partes principales: major.minor.patch
      for (let i = 0; i < 3; i++) {
        if (deploymentParts.main[i] > currentParts.main[i]) {
          logCheck(`📊 Deployment is newer (main version)`);
          return true;
        }
        if (deploymentParts.main[i] < currentParts.main[i]) {
          logCheck(`📊 Deployment is older (main version)`);
          return false;
        }
      }

      // Si las versiones principales son iguales, comparar prerelease
      if (deploymentParts.prerelease && currentParts.prerelease) {
        // Ambas son prerelease, comparar números
        if (
          deploymentParts.prerelease.number > currentParts.prerelease.number
        ) {
          logCheck(`📊 Deployment is newer (prerelease number)`);
          return true;
        }
      } else if (!deploymentParts.prerelease && currentParts.prerelease) {
        // Deployment es release, current es prerelease → deployment es más nueva
        logCheck(`📊 Deployment is newer (release vs prerelease)`);
        return true;
      }

      logCheck(`📊 Deployment is same or older`);
      return false;
    } catch (error) {
      logCheck('❌ Error comparing versions:');
      // En caso de error, asumir que hay nueva versión por seguridad
      return true;
    }
  }

  /**
   * Parsea una versión en sus componentes
   * Ejemplo: "2.0.3-beta.7" → { main: [2, 0, 3], prerelease: { type: "beta", number: 7 } }
   */
  private parseVersion(version: string): {
    main: number[];
    prerelease?: { type: string; number: number };
  } {
    const parts = version.split('-');
    const mainVersion = parts[0];
    const prereleaseVersion = parts[1];

    const main = mainVersion.split('.').map((num) => parseInt(num, 10) || 0);

    // Asegurar que tenemos 3 componentes (major, minor, patch)
    while (main.length < 3) {
      main.push(0);
    }

    let prerelease: { type: string; number: number } | undefined;
    if (prereleaseVersion) {
      const prereleaseMatch = prereleaseVersion.match(/^([a-zA-Z]+)\.?(\d+)?$/);
      if (prereleaseMatch) {
        prerelease = {
          type: prereleaseMatch[1],
          number: parseInt(prereleaseMatch[2] || '0', 10),
        };
      }
    }

    return { main, prerelease };
  }

  /**
   * Obtiene información de configuración actual
   */
  getDeploymentConfig(): GitHubDeploymentConfig | null {
    return this.deploymentConfig;
  }

  /**
   * Obtiene estado detallado del sistema de detección
   */
  getDetectionStatus(): object {
    const isRunning = !!this.autoCheckInterval;
    const config = this.deploymentConfig;

    const status = {
      isConfigured: !!config,
      isAutoCheckRunning: isRunning,
      intervalId: this.autoCheckInterval || 'none',
      lastDeploymentCheck: this.getLastDeploymentCheckTime(),
      lastDeploymentCheckFormatted: this.getLastDeploymentCheckTime()
        ? new Date(this.getLastDeploymentCheckTime()).toISOString()
        : 'never',
      config: config
        ? {
            repo: config.githubRepo,
            environment: config.environment,
            pollIntervalMinutes: (config.pollInterval || 0) / 60000,
            enableAutoCheck: config.enableAutoCheck,
            hasToken: !!config.githubToken,
          }
        : null,
    };

    logCheck('📊 Version Manager Detection Status:');
    logCheck(status);
    return status;
  }

  /**
   * Habilita/deshabilita el auto-check sin cambiar otras configuraciones
   */
  toggleAutoCheck(enable: boolean): void {
    if (this.deploymentConfig) {
      logCheck(
        `🔄 Toggling auto-check: ${this.deploymentConfig.enableAutoCheck} → ${enable}`
      );
      this.deploymentConfig.enableAutoCheck = enable;
      if (enable) {
        logCheck(`🚀 Enabling auto-check...`);
        this.startAutoVersionCheck();
      } else {
        logCheck(`⏸️ Disabling auto-check...`);
        this.stopAutoVersionCheck();
      }
      logCheck(`✅ Auto-check ${enable ? 'enabled' : 'disabled'} successfully`);
    } else {
      logCheck(`⚠️ Cannot toggle auto-check: no deployment config found`);
    }
  }
}

// Exportar instancia singleton
export const versionManager = VersionManager.getInstance();

// Función de utilidad para inicializar en la aplicación
export const initializeVersionManager = (
  store: Store<RootState>,
  userData?: AuthContextType | null
) => {
  logManager('🚀 ===== INITIALIZING VERSION MANAGER =====');
  versionManager.initialize(store, userData);

  // ✅ NO verificar versión al cargar - solo el polling maneja actualizaciones
  logManager('🔄 Version updates will be handled exclusively by polling');

  // Histórico: aquí se llamaba a `checkCacheCorruption()` cada 30 minutos via
  // setInterval. Se quitó la llamada porque los indicadores de "corrupción"
  // eran umbrales arbitrarios (Apollo cache > 5MB, sessionStorage > 20 forms
  // cacheados) que un usuario power-user golpea en uso normal. Resultado:
  // modal "Limpiar Caché — Corrupción de caché detectada" interrumpía la
  // sesión cada media hora aunque no hubiera corrupción real.
  //
  // El método se quedó "por si algún día", sin un solo llamante y —esto es lo
  // que lo hacía peligroso— sin el gate de producción que sí tienen los tres
  // caminos reales: cablearlo habría metido avisos de versión en beta. Se ha
  // borrado. Lo que queda para limpiar bajo criterio del usuario es el botón
  // "Limpiar caché" del menú de usuario (`forceCacheClear`).

  logManager('✅ Version Manager initialization completed');

  // LOG ESTADO DESPUÉS DE INIT (dar tiempo a que se configure)
  setTimeout(() => {
    const config = versionManager.getDeploymentConfig();
    const status = versionManager.getDetectionStatus();
    logManager('📊 ===== VERSION MANAGER STATUS AFTER INIT =====');
    logManager('Config:');
    logManager(config);
    logManager('Status:');
    logManager(status);
    logManager('============================================');
  }, 2000);

  // 🔍 Exponer funciones debug en consola (solo en desarrollo)
  if (import.meta.env.MODE === 'development') {
    (window as any).VersionManagerDebug = {
      getStatus: () => versionManager.getDetectionStatus(),
      checkNow: () => versionManager.checkGitHubDeployments(),
      forceCheck: () => versionManager.checkForNewDeployment(),
      toggleAutoCheck: (enable: boolean) =>
        versionManager.toggleAutoCheck(enable),
      getConfig: () => versionManager.getDeploymentConfig(),
      forceClear: (reason?: string) => versionManager.forceCacheClear(reason),
    };
    logManager('🔍 VersionManagerDebug functions available in console:');
    logManager('   - VersionManagerDebug.getStatus()');
    logManager('   - VersionManagerDebug.checkNow()');
    logManager('   - VersionManagerDebug.forceCheck()');
    logManager('   - VersionManagerDebug.toggleAutoCheck(true/false)');
    logManager('   - VersionManagerDebug.getConfig()');
    logManager('   - VersionManagerDebug.forceClear("reason")');
  }
};

